import type {
  IComboBoxClassNames,
  IComboBoxOptionStyles,
  IComboBoxStyles,
} from "@fluentui/react/lib/ComboBox";
import type { IStyle, ITheme } from "@fluentui/react/lib/Styling";
import {
  concatStyleSets,
  DefaultFontStyles,
  FontWeights,
  getHighContrastNoAdjustStyle,
  HighContrastSelector,
  mergeStyles,
  mergeStyleSets,
} from "@fluentui/react/lib/Styling";
import { memoizeFunction } from "@fluentui/react/lib/Utilities";

import type { StandardControlSemanticColors } from "@1js/acui-common";
import {
  baseFabricFocusAfterSelector,
  baseFabricFocusVisibleSelector,
  chevronButtonHighContrastInvertedStyling,
  commonCalloutBoxShadow,
  commonHighContrastDisabledStyles,
  commonHighContrastHoverFocusStyles,
  commonHighContrastOutlineFocusedDisabled,
  commonHighContrastOutlineHoverFocus,
  controlStylingConstants,
  getStandardControlSemanticColorsWithTheme,
  hiddenContentStyle,
  scaleBorderWithDpi,
  scalePropertyWithDpi,
  textContainerClassName,
} from "@1js/acui-common";

import type {
  AppComboBoxOptionInternalClassNames,
  AppComboBoxOptionInternalStyles,
  AppComboBoxStyles,
} from "./AppComboBox.Props";

export const maxContentWidth = 283; // The size used by the rich client
const comboBoxOptionHeight = 32;
const comboBoxCompoundOptionHeight = 50;

export interface AppComboBoxIconClassNames {
  controlAndIconContainer: string;
  icon: string;
}

export const getControlContainerStyles = memoizeFunction(
  (customStyles?: IStyle, identifiersAbove?: boolean): string => {
    return mergeStyles(
      {
        width: "inherit",
        display: "flex",
        alignItems: identifiersAbove ? "center" : "unset",
      },
      customStyles
    );
  }
);

const comboBoxRootHighContrastFocused = {
  color: "HighlightText",
  borderColor: "Highlight",
  backgroundColor: "Window",
};

export function getComboBoxContainerMarginStyles(
  rightMargin: number,
  pinToPreviousComboBox?: boolean
): IStyle {
  const { comboBoxBorderWidth } = controlStylingConstants;

  // If the combo box is pinned to the previous combo box, we apply a negative left margin
  // and mark it !important to override margins set by the parent Stack
  if (pinToPreviousComboBox) {
    return [
      {
        marginTop: 0,
        marginRight: `${rightMargin}px`,
        marginBottom: 0,
        marginLeft: `${-rightMargin - comboBoxBorderWidth}px !important`,
      },
      // The border does not scale linearly for DPI, so adjust the borderWidth
      // portion of the negative margin, to make sure they line up
      scalePropertyWithDpi(
        "marginLeft",
        (scaleFactor) =>
          `${-rightMargin - comboBoxBorderWidth * scaleFactor}px !important`
      ),
    ];
  }

  // Non-pinned combo boxes just have a right margin
  return { margin: `0 ${rightMargin}px 0 0px` };
}

export const getComboBoxIconClassNames = memoizeFunction(
  (
    showLabel: boolean | undefined,
    styles?: Partial<AppComboBoxStyles>,
    iconStyles?: IStyle,
    identifiersAbove?: boolean,
    disabled?: boolean
  ): AppComboBoxIconClassNames => {
    return mergeStyleSets(
      {
        controlAndIconContainer: {
          display: "flex",
          alignItems: "center",
        },
        icon: {
          width: 20,
          height: 20,
          marginLeft: identifiersAbove ? 0 : 10,
          marginRight: !showLabel ? 10 : undefined,
          paddingBottom: identifiersAbove ? 5 : undefined,
          opacity: disabled ? 0.4 : undefined,
        },
      },
      {
        controlAndIconContainer: styles && styles.controlAndIconContainer,
        icon: iconStyles,
      }
    );
  }
);

interface ComboBoxLabelClassNameParams {
  theme: ITheme;
  labelStyle?: Partial<IComboBoxStyles>;
  identifiersAbove?: boolean;
  disabled?: boolean;
  customStyles?: Partial<IComboBoxStyles> | undefined;
}

export const getComboxLabelClassname = memoizeFunction(
  (params: ComboBoxLabelClassNameParams): string => {
    const styles =
      params.labelStyle ||
      concatStyleSets(
        getLabelStyles(params.theme, params.identifiersAbove),
        params.customStyles
      );

    return mergeStyles(
      styles.label,
      { pointerEvents: "none" },
      params.disabled && styles.labelDisabled
    );
  }
);

const getLabelStyles = memoizeFunction(
  (theme: ITheme, identifiersAbove?: boolean): Partial<IComboBoxStyles> => {
    const standardControlSemanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);
    return {
      label: {
        marginLeft: identifiersAbove ? 0 : 10,
        marginRight: 10,
        fontWeight: FontWeights.regular,
      },
      labelDisabled: [
        {
          color: standardControlSemanticColors.inputTextDisabledColor,
          cursor: "default",
        },
        commonHighContrastDisabledStyles,
      ],
    };
  }
);

export const getTextContainerClassname = memoizeFunction(
  (customStyles?: IStyle): string => {
    return mergeStyles(customStyles, textContainerClassName);
  }
);

const getComboBoxStyles = memoizeFunction(
  (
    theme: ITheme,
    width?: number,
    pinToPreviousComboBox?: boolean,
    isOpen?: boolean,
    focused?: boolean,
    identifiersAbove?: boolean
  ): Partial<IComboBoxStyles> => {
    const widthToUse = width || 130;
    const standardControlSemanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);

    const commonFocusStyling = {
      borderColor: standardControlSemanticColors.inputFocusBorderColor,
      zIndex: 1,
    };

    const {
      comboBoxHeight,
      comboBoxCaretWidth,
      comboBoxBorderWidth,
      comboBoxRightMargin,
      comboBoxVerticalPadding,
    } = controlStylingConstants;

    return {
      container: [
        {
          display: "flex",
          alignItems: "center",
          height: controlStylingConstants.controlHeight,
        },
        getComboBoxContainerMarginStyles(
          comboBoxRightMargin,
          pinToPreviousComboBox
        ),
      ],
      root: [
        DefaultFontStyles.medium,
        {
          height: comboBoxHeight,
          width: widthToUse,
          lineHeight: comboBoxHeight - 2,
          paddingLeft: 0,
          paddingRight: comboBoxCaretWidth,
          borderColor: standardControlSemanticColors.inputBorderColor,
          zIndex: 0,
          boxShadow: "none",
          paddingTop: 0,
          paddingBottom: 0,
          position: "relative",
          outline: 0,
          userSelect: "none",
          backgroundColor: standardControlSemanticColors.inputBackgroundColor,
          borderWidth: comboBoxBorderWidth,
          borderStyle: "solid",
          cursor: "text",
          display: "block",
          overflow: "hidden",
          whiteSpace: "nowrap",
          boxSizing: "border-box",
          selectors: {
            input: {
              selectors: {
                "::-ms-clear": {
                  display: "none",
                },
              },
            },
            // Explicity set input padding styles to override styles from WordEditor.css
            // See Bug 2044255
            "input[type=text]": {
              padding: 0,
              paddingLeft: 4,
            },
            ".ms-Label": {
              display: "inline-block",
              marginBottom: 8,
            },
          },
        },
        isOpen && {
          selectors: {
            "&&": {
              // Increase specificity to override the focus styling because focus is always in the ComboBox
              borderColor: standardControlSemanticColors.inputFocusBorderColor,
            },
            [HighContrastSelector]: [
              comboBoxRootHighContrastFocused,
              {
                selectors: {
                  button: chevronButtonHighContrastInvertedStyling,
                },
              },
            ],
          },
        },

        scaleBorderWithDpi(comboBoxBorderWidth /* borderWidthAt100 */),
      ],
      rootDisabled: [
        commonHighContrastDisabledStyles,
        {
          backgroundColor:
            standardControlSemanticColors.disabledBackgroundColor,
          cursor: "default",
        },
        focused && [
          commonFocusStyling,
          commonHighContrastOutlineFocusedDisabled,
          {
            selectors: {
              [HighContrastSelector]: {
                outlineColor: "GrayText",
              },
            },
          },
        ],
      ],
      inputDisabled: [
        {
          color: standardControlSemanticColors.inputTextDisabledColor,
          selectors: {
            [HighContrastSelector]: {
              color: "GrayText",
              ...getHighContrastNoAdjustStyle(),
              backgroundColor: "ButtonFace",
              selectors: {
                "::selection": {
                  color: "GrayText !important",
                  backgroundColor: "Highlight",
                  ...getHighContrastNoAdjustStyle(),
                },
              },
            },
          },
        },
      ],
      ...getLabelStyles(theme, identifiersAbove),
      rootFocused: [
        commonFocusStyling,
        {
          selectors: {
            [HighContrastSelector]: [
              comboBoxRootHighContrastFocused,
              {
                selectors: {
                  ":hover button": chevronButtonHighContrastInvertedStyling,
                },
              },
            ],
            // Focus is always in the ComboBox even when navigating menu items. is-checked
            // is used to indicate if the menu is open.
            "button:not(.is-checked)": [
              {
                backgroundColor:
                  standardControlSemanticColors.hoverBackgroundColor,
                selectors: {
                  [HighContrastSelector]:
                    chevronButtonHighContrastInvertedStyling,
                },
              },
              commonHighContrastOutlineHoverFocus,
              commonHighContrastHoverFocusStyles,
            ],
          },
        },
        commonHighContrastOutlineHoverFocus,
      ],
      rootHovered: [
        {
          // Pinned combo boxes overlap their borders. Make sure the hovered one is in front so the border looks correct
          zIndex: 1,
        },
        {
          selectors: {
            button: [
              {
                background: standardControlSemanticColors.hoverBackgroundColor,
              },
              commonHighContrastOutlineHoverFocus,
              commonHighContrastHoverFocusStyles,
            ],
            [HighContrastSelector]: {
              color: "HighlightText",
              borderColor: "Highlight",
              backgroundColor: "Window",
              selectors: {
                button: chevronButtonHighContrastInvertedStyling,
              },
            },
          },
        },
        commonHighContrastOutlineHoverFocus,
      ],
      rootPressed: {
        selectors: {
          [HighContrastSelector]: comboBoxRootHighContrastFocused,
        },
      },
      input: {
        font: "inherit",
        color: standardControlSemanticColors.inputTextColor,
        height: comboBoxHeight - comboBoxVerticalPadding,
        lineHeight: comboBoxHeight - comboBoxVerticalPadding,
        backgroundColor: "transparent",
        boxSizing: "border-box",
        width: "100%",
        borderStyle: "none",
        outline: "none",
        textOverflow: "ellipsis",
        padding: 0,
        selectors: {
          "::selection": {
            backgroundColor:
              standardControlSemanticColors.inputSelectionBackgroundColor,
            color: standardControlSemanticColors.inputSelectionTextColor,
          },
        },
      },
      header: [
        DefaultFontStyles.medium,
        {
          fontWeight: 600,
          borderStyle: "none",
          height: 32,
          lineHeight: 32,
          cursor: "default",
          padding: "0px 12px",
          userSelect: "none",
          textAlign: "left",
          backgroundColor: standardControlSemanticColors.defaultBackgroundColor,
          color: standardControlSemanticColors.inputTextColor,
          fontSize: 14,
          maxWidth: maxContentWidth,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
        },
      ],
      callout: [
        commonCalloutBoxShadow,
        {
          border: "none",
        },
      ],
      optionsContainer: {
        display: "inline-block",
      },
      divider: {
        margin: "3px 8px",
        backgroundColor:
          standardControlSemanticColors.menuDividerBackgroundColor,
        display: "block",
        height: 1,
        position: "relative",
      },
      screenReaderText: hiddenContentStyle,
    };
  }
);

export const getComboBoxClassNames = memoizeFunction(
  (
    customStyles: Partial<IComboBoxStyles> | undefined,
    width?: number,
    pinToPreviousComboBox?: boolean,
    identifiersAbove?: boolean
  ): ((
    theme: ITheme,
    isOpen: boolean,
    disabled: boolean,
    required: boolean,
    focused: boolean,
    allowFreeForm: boolean,
    __: boolean
  ) => IComboBoxClassNames) => {
    return memoizeFunction(
      (
        theme: ITheme,
        isOpen: boolean,
        disabled: boolean,
        __: boolean,
        focused: boolean,
        allowFreeForm: boolean,
        ___: boolean
      ) => {
        const styles = concatStyleSets(
          getComboBoxStyles(
            theme,
            width,
            pinToPreviousComboBox,
            isOpen,
            focused,
            identifiersAbove
          ),
          customStyles
        );

        return {
          container: mergeStyles(
            styles.container,
            identifiersAbove && { display: "block" }
          ),
          // pointerEvents: 'none' will prevent the label in the ComboBox
          // from firing unwanted clicks on the inputs we get from Fabric.
          // These clicks are triggered by the for attribute in the Label component
          // that exists inside the ComboBox component.
          label: getComboxLabelClassname({
            theme,
            labelStyle: styles,
            disabled,
          }),
          root: mergeStyles(
            styles.root,
            !allowFreeForm && styles.rootDisallowFreeForm,
            !disabled && focused && styles.rootFocused,
            !disabled && {
              selectors: {
                ":hover": !isOpen && !focused && styles.rootHovered,
                ":active": styles.rootPressed,
                ":focus": styles.rootFocused,
              },
            },
            disabled && styles.rootDisabled
          ),
          errorMessage: "",
          rootError: "",
          input: mergeStyles(styles.input, disabled && styles.inputDisabled),
          callout: mergeStyles(styles.callout),
          optionsContainerWrapper: mergeStyles(styles.optionsContainerWrapper),
          optionsContainer: mergeStyles(styles.optionsContainer),
          header: mergeStyles(styles.header),
          divider: mergeStyles(styles.divider),
          screenReaderText: mergeStyles(styles.screenReaderText),
        };
      }
    );
  }
);

export const getComboBoxOptionClassNames = memoizeFunction(
  (
    styles: Partial<AppComboBoxOptionInternalStyles>
  ): AppComboBoxOptionInternalClassNames => {
    return {
      optionText: mergeStyles(styles.optionText),
      root: mergeStyles(styles.root, {
        selectors: {
          ":hover": styles.rootHovered,
          ":focus": styles.rootFocused,
          ":active": styles.rootPressed,
        },
      }),
      optionTextWrapper: mergeStyles(styles.optionTextWrapper),
      stackStyle: mergeStyles(styles.stackStyle),
      subTextStyle: mergeStyles(styles.subTextStyle),
    };
  }
);

export const getComboBoxOptionPadding = (
  dropdownHasHeaderOrIcons?: boolean,
  itemHasIcon?: boolean
): number => {
  // If there are headers or icons, we need to indent text only items so all items align
  if (dropdownHasHeaderOrIcons && !itemHasIcon) {
    return 40;
  }
  if (itemHasIcon) {
    return 10;
  }
  // If there are no headers or icons, use the default padding
  return 16;
};

/**
 * individualOptionOverrideStyles has precedence over baseOptionStyles.
 *
 * dropdownHasHeaderOrIcons: will be true if any item in the ComboBoxOptions is a header or contains an icon.
 * itemHasIcon: will be true if the individual item contains an icon.
 */
export const getComboBoxOptionStyles = memoizeFunction(
  (
    theme: ITheme,
    baseOptionStyles?: Partial<IComboBoxOptionStyles>,
    individualOptionOverrideStyles?: Partial<IComboBoxOptionStyles>,
    dropdownHasHeaderOrIcons?: boolean,
    itemHasIcon?: boolean,
    itemHasSubText?: boolean
  ): Partial<AppComboBoxOptionInternalStyles> => {
    const standardControlSemanticColors: StandardControlSemanticColors =
      getStandardControlSemanticColorsWithTheme(theme);
    /**
     * rootChecked: When an option is in focus or hovered on, we mark it as selected. This selection is
     * represented as a checked state on the button
     */
    const defaultStyles = {
      root: {
        height: itemHasSubText
          ? comboBoxCompoundOptionHeight
          : comboBoxOptionHeight,
        paddingLeft: getComboBoxOptionPadding(
          dropdownHasHeaderOrIcons,
          itemHasIcon
        ),
        paddingRight: getComboBoxOptionPadding(
          dropdownHasHeaderOrIcons,
          itemHasIcon
        ),
        maxWidth: maxContentWidth,
        minHeight: comboBoxOptionHeight,
      },
      header: {
        paddingLeft: 12,
      },
      rootHovered: {
        backgroundColor: standardControlSemanticColors.hoverBackgroundColor,
        selectors: {
          ":hover": commonHighContrastHoverFocusStyles,
        },
      },
      rootFocused: [
        {
          backgroundColor:
            standardControlSemanticColors.selectedBackgroundColor,
          selectors: {
            [baseFabricFocusAfterSelector]: [
              {
                border: `1px solid ${standardControlSemanticColors.selectedBorderColor}`,
                selectors: {
                  [HighContrastSelector]: [
                    {
                      border: "none",
                    },
                    commonHighContrastOutlineHoverFocus,
                    commonHighContrastHoverFocusStyles,
                  ],
                },
              },
            ],
          },
        },
        commonHighContrastOutlineHoverFocus,
        commonHighContrastHoverFocusStyles,
      ],
      rootChecked: [
        {
          backgroundColor:
            standardControlSemanticColors.selectedBackgroundColor,
          selectors: {
            [baseFabricFocusAfterSelector]: [
              {
                border: `1px solid ${standardControlSemanticColors.selectedBorderColor}`,
                selectors: {
                  [HighContrastSelector]: [
                    {
                      border: "none",
                    },
                    commonHighContrastOutlineHoverFocus,
                  ],
                },
              },
              commonHighContrastOutlineHoverFocus,
            ],
            [`${baseFabricFocusVisibleSelector} &:after`]: {
              outlineWidth: 2,
              left: 1,
              right: 1,
              top: 1,
              bottom: 1,
            },
          },
        },
        commonHighContrastOutlineHoverFocus,
        commonHighContrastHoverFocusStyles,
      ],
      rootCheckedHovered: {
        backgroundColor: standardControlSemanticColors.hoverBackgroundColor,
        selectors: {
          ":hover": commonHighContrastHoverFocusStyles,
        },
      },
      rootPressed: {
        backgroundColor: standardControlSemanticColors.pressedBackgroundColor,
      },
      rootCheckedPressed: {
        backgroundColor: standardControlSemanticColors.pressedBackgroundColor,
      },
      optionText: [
        {
          fontSize: 16,

          // At certain browser zooms and certain option styles, the text can get cut off slightly.
          overflow: "visible",
        },
      ],
      optionTextWrapper: {
        whiteSpace: "nowrap",
      },
      stackStyle: {
        overflow: "hidden",
      },
      subTextStyle: {
        fontSize: "11px",
        display: "block",
      },
    } as Partial<AppComboBoxOptionInternalStyles>;
    return mergeStyleSets(
      defaultStyles,
      baseOptionStyles,
      individualOptionOverrideStyles
    );
  }
);
